import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, FileText, FileDown, Trash2, Edit, Lock, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  exportEmploymentContractToPDF,
  exportContractAmendmentToPDF,
  exportCertificateToPDF,
  exportAnniversaryCertificateToPDF,
  exportWarningToPDF,
  exportVehicleAssignmentToPDF,
  exportKeyHandoverToPDF,
} from "@/lib/contractPdfExport";
import { saveDocumentToEmployeeFile, saveDocumentToApplicantFile, getDocumentCategory, getDocumentTypeName } from "@/lib/documentStorage";
import { ContractEditDialog } from "./ContractEditDialog";
import { useAuth } from "@/hooks/useAuth";

interface ContractTemplate {
  id: string;
  template_type: string;
  person_name: string;
  status: string;
  created_at: string;
  signed_at: string | null;
  position: string | null;
  base_salary: number | null;
  start_date: string | null;
  effective_date: string | null;
  certificate_title: string | null;
  years_of_service: number | null;
  warning_number: number | null;
  warning_reason: string | null;
  is_finalized: boolean | null;
  finalized_at: string | null;
  employee_id: string | null;
  applicant_id: string | null;
}

interface ContractsListProps {
  templateType: string;
}

export const ContractsList = ({ templateType }: ContractsListProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState<ContractTemplate[]>([]);
  const [companySettings, setCompanySettings] = useState<any>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<string>("");
  const [finalizingId, setFinalizingId] = useState<string | null>(null);

  useEffect(() => {
    fetchContracts();
  }, [templateType]);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      const [contractsRes, companyRes] = await Promise.all([
        supabase
          .from("contract_templates")
          .select("*")
          .eq("template_type", templateType)
          .order("created_at", { ascending: false }),
        supabase.from("company_settings").select("*").single()
      ]);

      if (contractsRes.data) setContracts(contractsRes.data);
      if (companyRes.data) setCompanySettings(companyRes.data);
    } catch (error) {
      console.error("Error fetching contracts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("contract_templates")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Dokument wurde gelöscht");
      fetchContracts();
    } catch (error) {
      console.error("Error deleting contract:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleEdit = (contractId: string) => {
    setSelectedContractId(contractId);
    setEditDialogOpen(true);
  };

  const handleFinalize = async (contract: ContractTemplate) => {
    if (!user) return;
    
    setFinalizingId(contract.id);
    try {
      // Fetch full contract data
      const { data } = await supabase
        .from("contract_templates")
        .select("*")
        .eq("id", contract.id)
        .single();

      if (!data) throw new Error("Dokument nicht gefunden");

      const companyAddress = companySettings 
        ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}`
        : "";

      let pdfDoc: any = null;
      let documentId: string | undefined;

      // Generate PDF based on template type
      switch (templateType) {
        case "employment_contract":
          pdfDoc = await exportEmploymentContractToPDF({
            personName: data.person_name,
            personAddress: `${data.person_address_street || ""}, ${data.person_address_zip || ""} ${data.person_address_city || ""}`,
            personBirthDate: data.person_birth_date,
            position: data.position || "",
            department: data.department || "",
            startDate: data.start_date || "",
            endDate: data.end_date || "",
            probationPeriodMonths: data.probation_period_months || 6,
            noticePeriodWeeks: data.notice_period_weeks || 4,
            baseSalary: data.base_salary || 0,
            salaryType: data.salary_type || "monthly",
            vacationDays: data.vacation_days || 30,
            weeklyHours: data.weekly_hours || 40,
            additionalTerms: data.custom_text || "",
            companyName: companySettings?.name || "",
            companyAddress,
            signatureUrl: data.signature_url,
          }, false);
          break;
        case "contract_amendment":
          pdfDoc = await exportContractAmendmentToPDF({
            personName: data.person_name,
            personAddress: data.person_address_street || "",
            oldPosition: data.old_position || "",
            newPosition: data.new_position || "",
            oldSalary: data.old_salary || 0,
            newSalary: data.new_salary || 0,
            salaryType: data.salary_type || "monthly",
            effectiveDate: data.effective_date || "",
            amendmentReason: data.amendment_reason || "",
            additionalChanges: data.custom_text || "",
            companyName: companySettings?.name || "",
            companyAddress,
            signatureUrl: data.signature_url,
          }, false);
          break;
        case "certificate":
          pdfDoc = await exportCertificateToPDF({
            personName: data.person_name,
            certificateType: data.certificate_type || "training",
            certificateTitle: data.certificate_title || "",
            certificateContent: data.certificate_content || "",
            issueDate: data.issue_date || "",
            companyName: companySettings?.name || "",
            companyAddress,
          }, false);
          break;
        case "anniversary":
          pdfDoc = await exportAnniversaryCertificateToPDF({
            personName: data.person_name,
            position: data.position || "",
            yearsOfService: data.years_of_service || 0,
            anniversaryDate: data.anniversary_date || "",
            customText: data.custom_text || "",
            companyName: companySettings?.name || "",
            companyAddress,
          }, false);
          break;
        case "warning":
          pdfDoc = await exportWarningToPDF({
            personName: data.person_name,
            personAddress: data.person_address_street || "",
            position: data.position || "",
            department: data.department || "",
            warningDate: data.warning_date || "",
            warningNumber: data.warning_number || 1,
            warningReason: data.warning_reason || "",
            warningDetails: data.warning_details || "",
            warningConsequences: data.warning_consequences || "",
            companyName: companySettings?.name || "",
            companyAddress,
            signatureUrl: data.signature_url,
          }, false);
          break;
        case "vehicle_assignment":
          // Parse vehicle data from custom_text (format: "manufacturer|license_plate")
          const vehicleData = (data.custom_text || "").split("|");
          pdfDoc = await exportVehicleAssignmentToPDF({
            personName: data.person_name,
            personAddress: `${data.person_address_street || ""}, ${data.person_address_zip || ""} ${data.person_address_city || ""}`,
            position: data.position || "",
            vehicleManufacturer: vehicleData[0] || "",
            vehicleLicensePlate: vehicleData[1] || "",
            effectiveDate: data.effective_date || "",
            companyName: companySettings?.name || "",
            companyAddress,
            signatureUrl: data.signature_url,
          }, false);
          break;
        case "key_handover": {
          let kh: any = {};
          try { kh = JSON.parse(data.custom_text || "{}"); } catch {}
          pdfDoc = await exportKeyHandoverToPDF({
            personName: data.person_name,
            employeeNumber: kh.employeeNumber || "",
            personAddressStreet: data.person_address_street || "",
            personAddressZip: data.person_address_zip || "",
            personAddressCity: data.person_address_city || "",
            closingTimes: kh.closingTimes || "",
            alarmPin: kh.alarmPin || "",
            keyType: kh.keyType || "Schlüssel",
            keyNumber: kh.keyNumber || "",
            keyDescription: kh.keyDescription || "",
            notes: kh.notes || "",
            documentDate: data.effective_date || data.issue_date || "",
            companyName: companySettings?.name || "",
            companyCity: companySettings?.city || "",
            signatureUrl: data.signature_url,
          }, false);
          break;
        }
      }

      // Save to employee/applicant documents
      if (pdfDoc) {
        const category = getDocumentCategory(templateType);
        const typeName = getDocumentTypeName(templateType);
        let description = `${typeName} - ${data.person_name}`;
        
        if (templateType === "warning") {
          description = `${typeName} Nr. ${data.warning_number} - ${data.warning_reason}`;
        } else if (templateType === "certificate") {
          description = `${typeName} - ${data.certificate_title}`;
        } else if (templateType === "key_handover") {
          let kh: any = {};
          try { kh = JSON.parse(data.custom_text || "{}"); } catch {}
          description = `${typeName} - ${kh.keyType || "Schlüssel"} ${kh.keyNumber || ""}`.trim();
        }

        if (data.employee_id) {
          const result = await saveDocumentToEmployeeFile(
            data.employee_id,
            description,
            category,
            pdfDoc,
            user.id
          );
          if (result.success) {
            documentId = result.documentId;
          }
        } else if (data.applicant_id) {
          const result = await saveDocumentToApplicantFile(
            data.applicant_id,
            description,
            "other",
            pdfDoc,
            user.id
          );
          if (result.success) {
            documentId = result.documentId;
          }
        }
      }

      // Update contract as finalized
      const { error } = await supabase
        .from("contract_templates")
        .update({
          is_finalized: true,
          finalized_at: new Date().toISOString(),
          finalized_by: user.id,
          employee_document_id: documentId || null,
          status: "finalized",
        })
        .eq("id", contract.id);

      if (error) throw error;

      toast.success("Dokument wurde festgesetzt und in der Mitarbeiterakte abgelegt");
      fetchContracts();
    } catch (error) {
      console.error("Error finalizing contract:", error);
      toast.error("Fehler beim Festsetzen des Dokuments");
    } finally {
      setFinalizingId(null);
    }
  };

  const handleExportPDF = async (contract: ContractTemplate) => {
    try {
      // Fetch full contract data
      const { data } = await supabase
        .from("contract_templates")
        .select("*")
        .eq("id", contract.id)
        .single();

      if (!data) return;

      const companyAddress = companySettings 
        ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}`
        : "";

      switch (templateType) {
        case "employment_contract":
          await exportEmploymentContractToPDF({
            personName: data.person_name,
            personAddress: `${data.person_address_street || ""}, ${data.person_address_zip || ""} ${data.person_address_city || ""}`,
            personBirthDate: data.person_birth_date,
            position: data.position || "",
            department: data.department || "",
            startDate: data.start_date || "",
            endDate: data.end_date || "",
            probationPeriodMonths: data.probation_period_months || 6,
            noticePeriodWeeks: data.notice_period_weeks || 4,
            baseSalary: data.base_salary || 0,
            salaryType: data.salary_type || "monthly",
            vacationDays: data.vacation_days || 30,
            weeklyHours: data.weekly_hours || 40,
            additionalTerms: data.custom_text || "",
            companyName: companySettings?.name || "",
            companyAddress,
            signatureUrl: data.signature_url,
          });
          break;
        case "contract_amendment":
          await exportContractAmendmentToPDF({
            personName: data.person_name,
            personAddress: data.person_address_street || "",
            oldPosition: data.old_position || "",
            newPosition: data.new_position || "",
            oldSalary: data.old_salary || 0,
            newSalary: data.new_salary || 0,
            salaryType: data.salary_type || "monthly",
            effectiveDate: data.effective_date || "",
            amendmentReason: data.amendment_reason || "",
            additionalChanges: data.custom_text || "",
            companyName: companySettings?.name || "",
            companyAddress,
            signatureUrl: data.signature_url,
          });
          break;
        case "certificate":
          await exportCertificateToPDF({
            personName: data.person_name,
            certificateType: data.certificate_type || "training",
            certificateTitle: data.certificate_title || "",
            certificateContent: data.certificate_content || "",
            issueDate: data.issue_date || "",
            companyName: companySettings?.name || "",
            companyAddress,
          });
          break;
        case "anniversary":
          await exportAnniversaryCertificateToPDF({
            personName: data.person_name,
            position: data.position || "",
            yearsOfService: data.years_of_service || 0,
            anniversaryDate: data.anniversary_date || "",
            customText: data.custom_text || "",
            companyName: companySettings?.name || "",
            companyAddress,
          });
          break;
        case "warning":
          await exportWarningToPDF({
            personName: data.person_name,
            personAddress: data.person_address_street || "",
            position: data.position || "",
            department: data.department || "",
            warningDate: data.warning_date || "",
            warningNumber: data.warning_number || 1,
            warningReason: data.warning_reason || "",
            warningDetails: data.warning_details || "",
            warningConsequences: data.warning_consequences || "",
            companyName: companySettings?.name || "",
            companyAddress,
            signatureUrl: data.signature_url,
          });
          break;
        case "vehicle_assignment":
          const vehicleDataExport = (data.custom_text || "").split("|");
          await exportVehicleAssignmentToPDF({
            personName: data.person_name,
            personAddress: `${data.person_address_street || ""}, ${data.person_address_zip || ""} ${data.person_address_city || ""}`,
            position: data.position || "",
            vehicleManufacturer: vehicleDataExport[0] || "",
            vehicleLicensePlate: vehicleDataExport[1] || "",
            effectiveDate: data.effective_date || "",
            companyName: companySettings?.name || "",
            companyAddress,
            signatureUrl: data.signature_url,
          });
          break;
        case "key_handover": {
          let kh: any = {};
          try { kh = JSON.parse(data.custom_text || "{}"); } catch {}
          await exportKeyHandoverToPDF({
            personName: data.person_name,
            employeeNumber: kh.employeeNumber || "",
            personAddressStreet: data.person_address_street || "",
            personAddressZip: data.person_address_zip || "",
            personAddressCity: data.person_address_city || "",
            closingTimes: kh.closingTimes || "",
            alarmPin: kh.alarmPin || "",
            keyType: kh.keyType || "Schlüssel",
            keyNumber: kh.keyNumber || "",
            keyDescription: kh.keyDescription || "",
            notes: kh.notes || "",
            documentDate: data.effective_date || data.issue_date || "",
            companyName: companySettings?.name || "",
            companyCity: companySettings?.city || "",
            signatureUrl: data.signature_url,
          });
          break;
        }
      }
      toast.success("PDF wurde erstellt");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim Erstellen des PDF");
    }
  };

  const getStatusBadge = (contract: ContractTemplate) => {
    if (contract.is_finalized) {
      return <Badge className="bg-green-600"><Lock className="h-3 w-3 mr-1" />Festgesetzt</Badge>;
    }
    switch (contract.status) {
      case "signed":
        return <Badge className="bg-blue-500">Unterschrieben</Badge>;
      case "pending_signature":
        return <Badge variant="secondary">Wartet auf Unterschrift</Badge>;
      case "archived":
        return <Badge variant="outline">Archiviert</Badge>;
      default:
        return <Badge variant="secondary">Entwurf</Badge>;
    }
  };

  const getTypeLabel = () => {
    switch (templateType) {
      case "employment_contract": return "Arbeitsverträge";
      case "contract_amendment": return "Vertragsänderungen";
      case "certificate": return "Zertifikate";
      case "anniversary": return "Jubiläumsurkunden";
      case "warning": return "Abmahnungen";
      case "vehicle_assignment": return "Fahrzeugüberlassungen";
      case "safety_instruction": return "Sicherheitsunterweisungen";
      case "key_handover": return "Schlüssel-/Transponder-Übergaben";
      default: return "Dokumente";
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Noch keine {getTypeLabel()} vorhanden</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Vorhandene {getTypeLabel()} ({contracts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {contracts.map((contract) => (
              <div key={contract.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{contract.person_name}</span>
                    {getStatusBadge(contract)}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {templateType === "employment_contract" && contract.position && (
                      <span>{contract.position} • </span>
                    )}
                    {templateType === "employment_contract" && contract.start_date && (
                      <span>Beginn: {format(new Date(contract.start_date), "dd.MM.yyyy", { locale: de })} • </span>
                    )}
                    {templateType === "contract_amendment" && contract.effective_date && (
                      <span>Gültig ab: {format(new Date(contract.effective_date), "dd.MM.yyyy", { locale: de })} • </span>
                    )}
                    {templateType === "certificate" && contract.certificate_title && (
                      <span>{contract.certificate_title} • </span>
                    )}
                    {templateType === "anniversary" && contract.years_of_service && (
                      <span>{contract.years_of_service} Jahre • </span>
                    )}
                    {templateType === "warning" && (
                      <span>Abmahnung Nr. {contract.warning_number} - {contract.warning_reason} • </span>
                    )}
                    {templateType === "vehicle_assignment" && contract.effective_date && (
                      <span>Gültig ab: {format(new Date(contract.effective_date), "dd.MM.yyyy", { locale: de })} • </span>
                    )}
                    <span>Erstellt: {format(new Date(contract.created_at), "dd.MM.yyyy", { locale: de })}</span>
                    {contract.is_finalized && contract.finalized_at && (
                      <span className="text-green-600"> • Festgesetzt: {format(new Date(contract.finalized_at), "dd.MM.yyyy", { locale: de })}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!contract.is_finalized && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(contract.id)}
                        className="gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Bearbeiten
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                            disabled={finalizingId === contract.id}
                          >
                            {finalizingId === contract.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                            Festsetzen
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Dokument festsetzen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Wenn Sie das Dokument festsetzen, wird es unveränderbar und automatisch in der Mitarbeiterakte abgelegt. 
                              Diese Aktion kann nicht rückgängig gemacht werden.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleFinalize(contract)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Festsetzen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportPDF(contract)}
                    className="gap-2"
                  >
                    <FileDown className="h-4 w-4" />
                    PDF
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Dokument löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {contract.is_finalized
                            ? "Achtung: Dieses Dokument wurde bereits festgesetzt. Beim Löschen wird der Vorlagen-Eintrag entfernt. Die bereits in der Mitarbeiterakte abgelegte PDF-Datei bleibt erhalten und muss bei Bedarf dort separat gelöscht werden. Diese Aktion kann nicht rückgängig gemacht werden."
                            : "Möchten Sie dieses Dokument wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(contract.id)}>
                          Löschen
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <ContractEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        contractId={selectedContractId}
        templateType={templateType}
        onSaved={fetchContracts}
      />
    </>
  );
};
